import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Box, Flex, Copy, themeColor, Button, Icon } from '@elparking/components'
import { FormattedMessage } from 'react-intl'
import IconContainer from './IconContainer'
import ExtraServiceBoxCurrency from './ExtraServiceBoxCurrency'

const ExtraServiceContainer = styled(Box)`
    padding: 16px;
    border: 1px solid ${({ highlighted }) => highlighted ? themeColor('accent10') : themeColor('gray30')};
    border-radius: 4px;
    background: ${({ highlighted }) => highlighted ? themeColor('accent10') : themeColor('white')};
    width: 100%;
    height: 100%;
`

ExtraServiceContainer.propTypes = {
    highlighted: PropTypes.bool,
}

const DescriptionParagraph = styled(Copy)`
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;

    &:hover {
        overflow: visible;
        -webkit-line-clamp: initial;
    }
`

const SimpleExtraServiceBox = ({
    title,
    description,
    icon,
}) => {
    return <ExtraServiceContainer highlighted>
        <Flex flexDirection='column' alignItems='flex-start' alignContent='space-between' height='100%'>
            <Box w='100%'>
                <Flex w='100%' flexWrap='wrap' mb='1em' alignItems='center'>
                    {icon && <IconContainer icon={icon} size='48px' title={title} />}
                    <Flex flexDirection='column' pl='1em' width='calc(100% - 48px)'>
                        <Copy padding='0' mr='0.5em' fontSize='medium' fontColor='accent_dark'>{title}</Copy>
                    </Flex>
                </Flex>
            </Box>
            <Box w='100%'>
                <Box display={['none', 'block', 'block']}>
                    <DescriptionParagraph fontColor='gray90' padding='0' fontSize='small' fontWeight='medium'>{description}</DescriptionParagraph>
                </Box>
                <Box display={['block', 'none', 'none']}>
                    <Copy fontColor='gray90' fontSize='xSmall' padding='0' fontWeight='medium'>{description}</Copy>
                </Box>
            </Box>
        </Flex>
    </ ExtraServiceContainer>
}

SimpleExtraServiceBox.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    icon: PropTypes.string,
}

const ExtraServiceBox = ({
    title,
    included,
    subtitle,
    description,
    icon,
    price,
    defaultUnits,
    onChange,
    squared,
    disabled,
    qtyMax,
}) => {
    const [showDescription, setShowDescription] = useState(false)
    const SquaredExtraServiceBox = () =>
        <Flex flexDirection='column' alignItems='flex-start' alignContent='space-between' height='100%'>
            <Box w='100%'>
                <Flex w='100%' flexWrap='wrap' mb='1em' alignItems='center'>
                    {icon && <IconContainer icon={icon} size='48px' title={title} />}
                    <Flex flexDirection='column' width='calc(100% - 48px)' pl='1em'>
                        <Flex flexDirection={['row', 'column']} width='100%' justifyContent='space-between'>
                            <Copy as='h3' padding='0' mr='0.5em' fontSize='medium' fontColor='accent_dark'>{title}</Copy>
                            {subtitle && <Copy as='p' padding='0' fontSize='small' fontColor='darkGray'>{subtitle}</Copy>}
                            {included && <Copy padding='0' fontSize='small' fontColor='darkGray'>
                                <FormattedMessage defaultMessage='¡Incluido!' />
                            </Copy>}
                        </Flex>
                        <Box display={['block', 'none', 'none']}>
                            <Button.unstyled fontColor='main' textTransform='none' fontSize={['small', 'medium']} width='100%' onClick={() => setShowDescription(!showDescription)}>
                                <Flex justifyContent='space-between'>
                                    <FormattedMessage defaultMessage='¿Qué incluye?' />
                                    <Icon fontColor='inherit' type={showDescription ? 'arrow-up' : 'arrow-down'} />
                                </Flex>
                            </Button.unstyled>
                        </Box>
                    </Flex>
                </Flex>
                <Box w='100%'>
                    <Box display={['none', 'block', 'block']}>
                        <DescriptionParagraph fontColor='gray90' padding='0' fontSize='small' fontWeight='medium'>{description }</DescriptionParagraph>
                    </Box>
                    <Box display={['block', 'none', 'none']}>
                        {showDescription && <Copy fontColor='gray90' fontSize='xSmall' mt='1em' padding='0' fontWeight='medium'>{description}</Copy>}
                    </Box>
                </Box>
            </Box>
            <Box width='100%'>
                {price && <Flex justifyContent='space-between' flexWrap='wrap' textAlign='right' w='100%' mt='1em'>
                    <Copy fontSize='medium' mr='0.5em' fontWeight='bold'>
                        <FormattedMessage defaultMessage='{price}' values={{ price }} />
                    </Copy>
                    <ExtraServiceBoxCurrency fontSize='large' min={0} disabled={disabled} defaultUnits={defaultUnits} onChange={onChange} qtyMax={qtyMax}/>
                </Flex>}
            </Box>
        </Flex>

    const RectangularExtraServiceBox = () => <Flex height='100%' alignItems='center' justifyContent='space-between' flexDirection={['column', 'row']}>
        <Flex width={['100%', '70%']} mr={['0', '0.5em']} flexDirection={['column', 'row']}>
            <IconContainer icon={icon} size='48px' title={title}/>
            <Box pl={['0', '1em']} width={['100%', 'calc(100% - 48px)']}>
                <Flex mb={['0', '0.5em']} alignItems='baseline' flexDirection={['column', 'row']}>
                    <Copy as='h3' padding='0' mr={['0', '0.5em']} fontSize={['medium', 'large']} fontColor='accent_dark'>{title}</Copy>
                    {subtitle && <Copy padding='0' fontSize='small' fontColor='darkGray'>{subtitle}</Copy>}
                </Flex>
                <DescriptionParagraph padding='0' fontSize='small' fontColor='gray90' fontWeight='medium'>{description}</DescriptionParagraph>
            </Box>
        </Flex>
        {price && <Flex w={['100%', '30%']} flexDirection={['column', 'row']} justifyContent='space-between' alignItems={['start', 'center']} flexWrap='wrap' textAlign='right'>
            <Copy fontSize='small' mr={['0', '0.5em']} fontWeight='bold'>
                <FormattedMessage defaultMessage='{price}' values={{ price }} />
            </Copy>
            <ExtraServiceBoxCurrency fontSize='medium' min={0} disabled={disabled} defaultUnits={defaultUnits} onChange={onChange} qtyMax={qtyMax} />
        </Flex>}
    </Flex>

    return (
        <ExtraServiceContainer highlighted={included}>
            {squared ? <SquaredExtraServiceBox /> : <RectangularExtraServiceBox/>}
        </ ExtraServiceContainer>

    )
}

ExtraServiceBox.propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    description: PropTypes.string,
    icon: PropTypes.string,
    price: PropTypes.string,
    defaultUnits: PropTypes.number,
    onChange: PropTypes.func,
    squared: PropTypes.bool,
    qtyMax: PropTypes.number,
    included: PropTypes.bool,
    disabled: PropTypes.bool,
}

ExtraServiceBox.defaultProps = {
    highlighted: false,
    squared: false,
    defaultUnits: 0,
}

export { SimpleExtraServiceBox }
export default ExtraServiceBox
